<template>
  <v-container id="users" fluid tag="section" class="us-page dashboard-container" style="min-height: 100vh;">

    <!-- ── Cabeçalho ─────────────────────────────── -->
    <div class="d-flex align-center justify-space-between mb-5">
      <div class="d-flex align-center">
        <div class="us-header-icon mr-3">
          <v-icon color="#22d3ee" size="22">mdi-account-supervisor-circle</v-icon>
        </div>
        <div>
          <div class="us-page-title">{{ $t('users.title') }}</div>
          <div class="us-page-sub">{{ users.length }} usuários · gerencie perfis, times e acessos</div>
        </div>
      </div>
      <div class="d-flex align-center" style="gap:10px;">
        <v-btn
          v-if="!isViewOnlyMode"
          outlined
          small
          class="us-btn-import text-none"
          @click="importDialog = true"
        >
          <v-icon left small>mdi-file-upload-outline</v-icon>
          {{ $t('users.import') }}
        </v-btn>
        <v-btn
          v-if="!isViewOnlyMode"
          depressed
          class="us-btn-new text-none"
          @click="openCreateDialog"
        >
          <v-icon left small>mdi-plus</v-icon>
          {{ $t('users.new') }}
        </v-btn>
      </div>
    </div>

    <!-- ── Filtros ────────────────────────────────── -->
    <div class="d-flex align-center mb-4 us-filters">
      <v-text-field
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        :placeholder="$t('users.searchPlaceholder')"
        outlined
        dense
        hide-details
        clearable
        class="us-search mr-3"
        @input="debouncedSearch"
      />
      <v-select
        v-model="filterDepartment"
        :items="departments"
        item-text="name"
        item-value="id"
        label="Departamento"
        outlined
        dense
        hide-details
        clearable
        class="us-filter mr-3"
        append-icon="mdi-chevron-down"
        @change="loadUsers"
      />
      <v-select
        v-model="filterRole"
        :items="roleFilterOptions"
        label="Perfil/Role"
        outlined
        dense
        hide-details
        clearable
        class="us-filter"
        append-icon="mdi-chevron-down"
        @change="loadUsers"
      />
    </div>

    <!-- ── Tabela (sem card) ──────────────────────── -->
    <v-data-table
      :headers="headers"
      :items="users"
      :loading="loading"
      :search="search"
      class="us-table elevation-0"
    >
      <!-- Avatar e Nome -->
      <template v-slot:item.name="{ item }">
        <div class="d-flex align-center py-2">
          <v-avatar
            size="36"
            :color="item.avatarUrl ? 'transparent' : getAvatarColor(item.name)"
            class="mr-3"
          >
            <v-img v-if="item.avatarUrl" :src="item.avatarUrl" :alt="item.name" />
            <span v-else class="white--text text-caption font-weight-bold">{{ getInitials(item.name) }}</span>
          </v-avatar>
          <div>
            <div class="font-weight-medium us-user-name">{{ item.name }}</div>
            <div class="text-caption us-user-email">{{ item.email }}</div>
          </div>
        </div>
      </template>

      <!-- Departamento -->
      <template v-slot:item.departmentName="{ item }">
        <span v-if="item.department && item.department.name">{{ item.department.name }}</span>
        <span v-else class="us-empty">—</span>
      </template>

      <!-- Cargo -->
      <template v-slot:item.jobRoleName="{ item }">
        <span v-if="item.jobRole && item.jobRole.title">{{ item.jobRole.title }}</span>
        <span v-else class="us-empty">—</span>
      </template>

      <!-- Times -->
      <template v-slot:item.teamName="{ item }">
        <div v-if="item.teams && item.teams.length > 0" class="d-flex align-center" style="gap:4px; flex-wrap:wrap;">
          <v-chip
            v-for="team in item.teams.slice(0, 2)"
            :key="team.id"
            x-small
            :color="team.color || '#1f2d3a'"
            dark
            class="font-weight-medium"
          >
            {{ team.name }}
          </v-chip>
          <v-tooltip v-if="item.teams.length > 2" top>
            <template v-slot:activator="{ on, attrs }">
              <v-chip x-small color="grey darken-2" dark v-bind="attrs" v-on="on">+{{ item.teams.length - 2 }}</v-chip>
            </template>
            <div>
              <div v-for="team in item.teams.slice(2)" :key="team.id" class="text-caption">• {{ team.name }}</div>
            </div>
          </v-tooltip>
        </div>
        <span v-else class="us-empty">—</span>
      </template>

      <!-- Role/Perfil -->
      <template v-slot:item.role="{ item }">
        <v-chip :color="getRoleColor(item.role)" x-small dark class="font-weight-bold">
          {{ getRoleLabel(item.role) }}
        </v-chip>
      </template>

      <!-- Status -->
      <template v-slot:item.status="{ item }">
        <div class="d-flex align-center">
          <span
            class="us-status-dot mr-1"
            :style="{ backgroundColor: item.status === 'ACTIVE' ? '#22c55e' : item.status === 'PENDING' ? '#f59e0b' : '#6b7280' }"
          ></span>
          <span class="us-status-text" :style="{ color: item.status === 'ACTIVE' ? '#4ade80' : item.status === 'PENDING' ? '#fbbf24' : '#9ca3af' }">
            {{ getStatusLabel(item.status) }}
          </span>
        </div>
      </template>

      <!-- Ações -->
      <template v-slot:item.actions="{ item }">
        <div class="d-flex align-center justify-center" style="gap:2px;">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon x-small v-bind="attrs" v-on="on" :disabled="!canEditUser(item)" @click="editUser(item)" class="us-action-btn">
                <v-icon size="16">mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ canEditUser(item) ? 'Editar' : 'Sem permissão para editar' }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon x-small v-bind="attrs" v-on="on" :disabled="!canEditUser(item)" @click="toggleUserStatus(item)" class="us-action-btn">
                <v-icon size="16">{{ item.status === 'ACTIVE' ? 'mdi-account-off-outline' : 'mdi-account-check-outline' }}</v-icon>
              </v-btn>
            </template>
            <span>{{ canEditUser(item) ? (item.status === 'ACTIVE' ? 'Desativar' : 'Ativar') : 'Sem permissão' }}</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>

    <!-- ── Dialogs ───────────────────────────────── -->
    <!-- Dialog de Criar/Editar -->
    <v-dialog
      v-model="dialog"
      max-width="700px"
      persistent
    >
      <v-card>
        <v-card-title style="background-color: #22d3ee" class="white--text" :class="{ 'us-dialog-header': true }">
          <span class="headline">
            <v-icon left dark>mdi-account</v-icon>
            {{ editMode ? 'Editar Usuário' : 'Novo Usuário' }}
          </span>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-form ref="form" v-model="validForm">
            <v-row>
              <!-- Seletor de Tenant (apenas para PLATFORM_OWNER) -->
              <v-col v-if="currentUserRole === 'PLATFORM_OWNER'" cols="12">
                <v-alert
                  type="info"
                  text
                  dense
                  class="mb-4"
                >
                  <v-icon small left>mdi-information</v-icon>
                  Como Proprietário da Plataforma, você pode criar usuários em qualquer tenant
                </v-alert>
                
                <v-autocomplete
                  v-model="editedItem.tenantId"
                  :items="tenants"
                  item-text="name"
                  item-value="id"
                  label="Tenant/Organização *"
                  :rules="[rules.required]"
                  prepend-icon="mdi-domain"
                  outlined
                  dense
                  :loading="loadingTenants"
                  hint="Selecione o tenant onde o usuário será criado"
                  persistent-hint
                  @change="onTenantChange"
                >
                  <template v-slot:selection="{ item }">
                    <v-chip small color="primary" dark>
                      <v-icon small left>mdi-domain</v-icon>
                      {{ item.name }}
                    </v-chip>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        ID: {{ item.id }} | Status: {{ item.status }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>

                <v-divider class="my-4" />
              </v-col>

              <!-- Nome -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.name"
                  label="Nome Completo *"
                  :rules="[rules.required]"
                  prepend-icon="mdi-account"
                  outlined
                  dense
                  placeholder="Ex: João da Silva"
                />
              </v-col>

              <!-- E-mail -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.email"
                  label="E-mail *"
                  :rules="[rules.required, rules.email]"
                  prepend-icon="mdi-email"
                  outlined
                  dense
                  type="email"
                  placeholder="joao@empresa.com"
                  :disabled="editMode"
                />
              </v-col>

              <!-- Departamento -->
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="editedItem.departmentId"
                  :items="departments"
                  item-text="name"
                  item-value="id"
                  label="Departamento"
                  prepend-icon="mdi-office-building"
                  outlined
                  dense
                  clearable
                  :loading="loadingDepartments"
                  @change="onDepartmentChange"
                >
                  <template v-slot:selection="{ item }">
                    {{ item.name }}
                  </template>
                  <template v-slot:item="{ item }">
                    {{ item.name }}
                  </template>
                </v-autocomplete>
              </v-col>

              <!-- Cargo -->
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="editedItem.jobRoleId"
                  :items="filteredJobRoles"
                  item-text="title"
                  item-value="id"
                  label="Cargo"
                  prepend-icon="mdi-briefcase"
                  outlined
                  dense
                  clearable
                  :loading="loadingJobRoles"
                  :disabled="!editedItem.departmentId"
                  :hint="!editedItem.departmentId ? 'Selecione um departamento primeiro' : ''"
                  persistent-hint
                >
                  <template v-slot:selection="{ item }">
                    {{ item.title }}
                  </template>
                  <template v-slot:item="{ item }">
                    {{ item.title }}
                  </template>
                </v-autocomplete>
              </v-col>

              <!-- Times/Squads -->
              <v-col v-if="editMode && editedItem.teams && editedItem.teams.length > 0" cols="12">
                <div class="mb-2">
                  <label class="text-caption grey--text text--darken-1 d-flex align-center">
                    <v-icon x-small class="mr-1">mdi-account-group</v-icon>
                    Times/Squads
                  </label>
                  <div class="d-flex align-center mt-2" style="gap: 8px; flex-wrap: wrap">
                    <v-chip
                      v-for="team in editedItem.teams"
                      :key="team.id"
                      small
                      :color="team.color || '#FF771C'"
                      dark
                    >
                      <v-icon small left>{{ team.icon || 'mdi-account-group' }}</v-icon>
                      {{ team.name }}
                    </v-chip>
                  </div>
                  <p class="text-caption grey--text mt-2 mb-0">
                    <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
                    Gerenciado na tela de Times
                  </p>
                </div>
              </v-col>

              <!-- Role/Perfil -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.role"
                  :items="availableRoleOptions"
                  label="Perfil/Role *"
                  :rules="[rules.required]"
                  prepend-icon="mdi-shield-account"
                  outlined
                  dense
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.text }}</v-list-item-title>
                      <v-list-item-subtitle class="text-caption">{{ item.description }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-select>
              </v-col>

              <!-- Status -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.status"
                  :items="statusOptions"
                  item-text="label"
                  item-value="value"
                  label="Status *"
                  prepend-icon="mdi-account-check"
                  :rules="[rules.required]"
                  outlined
                  dense
                >
                  <template v-slot:selection="{ item }">
                    <span :style="{ color: getStatusColor(item.value), fontWeight: 500 }">
                      {{ item.label }}
                    </span>
                  </template>
                  <template v-slot:item="{ item }">
                    <span :style="{ color: getStatusColor(item.value), fontWeight: 500 }">
                      {{ item.label }}
                    </span>
                  </template>
                </v-select>
              </v-col>

              <!-- Gestor -->
              <v-col cols="12" md="6" v-if="editedItem.role === 'MANAGER' || editedItem.role === 'ADMIN'">
                <v-autocomplete
                  v-model="editedItem.managerId"
                  :items="managers"
                  item-text="name"
                  item-value="id"
                  label="Gestor Direto"
                  prepend-icon="mdi-account-supervisor"
                  outlined
                  dense
                  clearable
                  :loading="loadingManagers"
                />
              </v-col>

              <!-- Telefone -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.phone"
                  label="Telefone/WhatsApp"
                  prepend-icon="mdi-phone"
                  outlined
                  dense
                  placeholder="+55 11 99999-8888"
                  :rules="[rules.phoneFormat]"
                  hint="Formato: +55 DDD Número"
                  persistent-hint
                  @input="formatPhoneInput"
                />
              </v-col>

              <!-- Data de Contratação -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.hireDate"
                  type="date"
                  label="Data de Contratação"
                  prepend-inner-icon="mdi-calendar"
                  outlined
                  dense
                  clearable
                  hide-details="auto"
                  :max="maxHireDate"
                  hint="Não é permitido selecionar data futura"
                  persistent-hint
                />
              </v-col>
            </v-row>
          </v-form>

          <v-alert
            v-if="errorMessage"
            type="error"
            dismissible
            class="mt-4"
          >
            {{ errorMessage }}
          </v-alert>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="closeDialog"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="#22d3ee"
            dark
            :loading="saving"
            :disabled="!validForm || isViewOnlyMode"
            @click="saveUser"
          >
            <v-icon left>mdi-content-save</v-icon>
            {{ editMode ? 'Salvar' : 'Criar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Importar CSV -->
    <v-dialog
      v-model="importDialog"
      max-width="600px"
      persistent
    >
      <v-card>
        <v-card-title class="info white--text">
          <v-icon left dark>mdi-file-upload</v-icon>
          Importar Usuários via CSV
        </v-card-title>

        <v-card-text class="pt-6">
          <v-alert type="info" text class="mb-4">
            <div class="font-weight-bold mb-2">Formato do CSV:</div>
            <pre class="text-caption">email,name,role
joao@empresa.com,João Silva,USER
maria@empresa.com,Maria Santos,USER
admin@empresa.com,Admin Sistema,USER</pre>
          </v-alert>

          <v-file-input
            v-model="csvFile"
            label="Selecione o arquivo CSV"
            prepend-icon="mdi-file-delimited"
            accept=".csv,text/csv"
            outlined
            dense
            :rules="[rules.fileRequired]"
            @change="onFileChange"
          />

          <v-textarea
            v-model="csvContent"
            label="Ou cole o conteúdo do CSV aqui"
            outlined
            rows="8"
            placeholder="email,name,role&#10;joao@empresa.com,João Silva,USER"
          />

          <v-alert
            v-if="importResult"
            :type="importResult.type"
            dismissible
            class="mt-4"
          >
            {{ importResult.message }}
          </v-alert>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-btn
            text
            color="grey"
            @click="downloadTemplate"
          >
            <v-icon left>mdi-download</v-icon>
            Baixar Template
          </v-btn>
          <v-spacer />
          <v-btn
            text
            @click="closeImportDialog"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="info"
            :loading="importing"
            :disabled="(!csvFile && !csvContent) || isViewOnlyMode"
            @click="importUsers"
          >
            <v-icon left>mdi-upload</v-icon>
            Importar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Confirmação de Status -->
    <v-dialog
      v-model="statusDialog"
      max-width="500px"
      persistent
    >
      <v-card rounded="lg" class="overflow-hidden">
        <div 
          :style="{ background: statusAction === 'ACTIVE' ? '#4CAF50' : '#F44336', padding: '16px 20px' }" 
          class="d-flex align-center justify-space-between"
        >
          <div class="d-flex align-center">
            <v-icon dark left>{{ statusAction === 'ACTIVE' ? 'mdi-account-check' : 'mdi-account-off' }}</v-icon>
            <span class="white--text text-h6 font-weight-bold">
              {{ statusAction === 'ACTIVE' ? 'Ativar Usuário' : 'Desativar Usuário' }}
            </span>
          </div>
        </div>

        <v-card-text class="pa-6">
          <div class="text-center mb-4">
            <v-icon :size="64" :color="statusAction === 'ACTIVE' ? '#4CAF50' : '#F44336'">
              {{ statusAction === 'ACTIVE' ? 'mdi-account-check-outline' : 'mdi-account-off-outline' }}
            </v-icon>
          </div>
          
          <div class="text-h6 text-center mb-3">
            {{ statusAction === 'ACTIVE' ? 'Tem certeza que deseja ativar este usuário?' : 'Tem certeza que deseja desativar este usuário?' }}
          </div>
          
          <v-alert
            :type="statusAction === 'ACTIVE' ? 'success' : 'warning'"
            text
            dense
            class="mb-4"
          >
            <div class="font-weight-bold mb-1">{{ userToToggle?.name }}</div>
            <div class="text-caption">{{ userToToggle?.email }}</div>
          </v-alert>
          
          <div class="text-body-2 grey--text text--darken-1">
            {{ statusAction === 'ACTIVE' 
              ? 'O usuário terá acesso ao sistema novamente.' 
              : 'O usuário não poderá mais acessar o sistema.'
            }}
          </div>
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-spacer />
          <v-btn
            text
            @click="statusDialog = false"
            :disabled="togglingStatus"
          >
            Cancelar
          </v-btn>
          <v-btn
            :color="statusAction === 'ACTIVE' ? '#4CAF50' : '#F44336'"
            dark
            :loading="togglingStatus"
            @click="confirmToggleStatus"
          >
            <v-icon left>{{ statusAction === 'ACTIVE' ? 'mdi-check' : 'mdi-close' }}</v-icon>
            {{ statusAction === 'ACTIVE' ? 'Ativar' : 'Desativar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar de Feedback -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      top
      right
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { UsersService, OrganizationService } from '@/services'
import TenantsService from '@/services/tenants.service'

export default {
  name: 'UsersView',

  data: () => ({
    // Listagem
    users: [],
    loading: false,
    search: '',
    filterDepartment: null,
    filterRole: null,

    // Tabela
    headers: [
      { text: 'Usuário', value: 'name', sortable: true },
      { text: 'Departamento', value: 'departmentName', sortable: true },
      { text: 'Cargo', value: 'jobRoleName', sortable: true },
      { text: 'Time', value: 'teamName', sortable: true },
      { text: 'Perfil', value: 'role', sortable: true },
      { text: 'Status', value: 'status', sortable: true },
      { text: 'Ações', value: 'actions', sortable: false, align: 'center' },
    ],

    // Dialog de Criar/Editar
    dialog: false,
    editMode: false,
    validForm: false,
    saving: false,
    errorMessage: '',
    
    editedItem: {
      name: '',
      email: '',
      departmentId: null,
      jobRoleId: null,
      role: 'USER',
      status: 'ACTIVE',
      managerId: null,
      phone: '',
      hireDate: null,
      tenantId: null, // ✅ Campo para PLATFORM_OWNER selecionar tenant
      teams: [], // ✅ Times do usuário (somente leitura)
    },
    
    defaultItem: {
      name: '',
      email: '',
      departmentId: null,
      jobRoleId: null,
      role: 'USER',
      status: 'ACTIVE',
      managerId: null,
      phone: '',
      hireDate: null,
      tenantId: null, // ✅ Campo para PLATFORM_OWNER selecionar tenant
      teams: [], // ✅ Times do usuário (somente leitura)
    },

    // Opções
    statusOptions: [
      { label: 'Ativo', value: 'ACTIVE' },
      { label: 'Desativado', value: 'DISABLED' },
      { label: 'Pendente', value: 'PENDING' },
    ],

    roleOptions: [
      { 
        text: 'Proprietário da Plataforma', 
        value: 'PLATFORM_OWNER',
        description: 'Acesso total à plataforma e todos os tenants'
      },
      { 
        text: 'Administrador de Parceiro', 
        value: 'PARTNER_ADMIN',
        description: 'Gerencia múltiplos tenants como parceiro'
      },
      { 
        text: 'Admin', 
        value: 'TENANT_ADMIN',
        description: 'Administrador da organização com acesso total'
      },
      { 
        text: 'Gestor', 
        value: 'MANAGER',
        description: 'Gerencia equipe e cria novos usuários'
      },
      { 
        text: 'Usuário', 
        value: 'USER',
        description: 'Colaborador padrão com acesso básico'
      },
    ],

    // Departamentos e Cargos
    departments: [],
    jobRoles: [],
    filteredJobRoles: [],
    managers: [],
    loadingDepartments: false,
    loadingJobRoles: false,
    loadingManagers: false,

    // Tenants (para PLATFORM_OWNER)
    tenants: [],
    loadingTenants: false,

    // Importação CSV
    importDialog: false,
    csvFile: null,
    csvContent: '',
    importing: false,
    importResult: null,

    // Dialog de Confirmação de Status
    statusDialog: false,
    userToToggle: null,
    statusAction: null, // 'ACTIVE' ou 'DISABLED'
    togglingStatus: false,

    // Validações
    rules: {
      required: v => !!v || 'Campo obrigatório',
      email: v => /.+@.+\..+/.test(v) || 'E-mail inválido',
      fileRequired: v => !!v || 'Selecione um arquivo',
      phoneFormat: v => {
        if (!v) return true // Campo opcional
        // Remove espaços e caracteres especiais para validar
        const cleaned = v.replace(/[\s()-]/g, '')
        // Formato: +5511999998888 (13 dígitos: +55 + DDD(2) + Número(8 ou 9))
        const isValid = /^\+55\d{10,11}$/.test(cleaned)
        return isValid || 'Formato: +55 DDD Número (ex: +55 11 99999-8888)'
      },
    },

    // Debounce
    searchTimeout: null,

    // Snackbar
    snackbar: {
      show: false,
      text: '',
      color: 'success',
    },
  }),

  computed: {
    // Data máxima permitida para contratação (hoje)
    maxHireDate() {
      return new Date().toISOString().split('T')[0]
    },

    // Usuário logado
    currentUser() {
      try {
        const userStr = localStorage.getItem('user')
        return userStr ? JSON.parse(userStr) : null
      } catch (error) {
        console.error('Erro ao recuperar usuário:', error)
        return null
      }
    },

    // Role do usuário logado
    currentUserRole() {
      return this.currentUser?.role || 'USER'
    },

    // Verifica se o usuário pode acessar a tela de gestão de usuários
    canAccessUserManagement() {
      const allowedRoles = ['PLATFORM_OWNER', 'PARTNER_ADMIN', 'TENANT_ADMIN', 'MANAGER']
      return allowedRoles.includes(this.currentUserRole)
    },

    // Opções de role disponíveis baseado no usuário logado (para CRIAR usuários)
    availableRoleOptions() {
      const role = this.currentUserRole

      // PLATFORM_OWNER: Pode criar todos os tipos de usuário
      if (role === 'PLATFORM_OWNER') {
        return this.roleOptions
      }

      // PARTNER_ADMIN: Pode criar TENANT_ADMIN, MANAGER e USER
      if (role === 'PARTNER_ADMIN') {
        return this.roleOptions.filter(r => 
          ['TENANT_ADMIN', 'MANAGER', 'USER'].includes(r.value)
        )
      }

      // TENANT_ADMIN: Pode criar MANAGER e USER
      if (role === 'TENANT_ADMIN') {
        return this.roleOptions.filter(r => 
          ['MANAGER', 'USER'].includes(r.value)
        )
      }

      // MANAGER: Pode criar MANAGER (subordinado) e USER
      if (role === 'MANAGER') {
        return this.roleOptions.filter(r =>
          ['MANAGER', 'USER'].includes(r.value)
        )
      }

      // USER: Não pode criar ninguém (não deve ter acesso à tela)
      return []
    },

    isViewOnlyMode() {
      return this.$store.getters.isViewOnlyMode
    },

    // Opções de role para FILTRO (mostra todas as roles EXCETO PLATFORM_OWNER)
    roleFilterOptions() {
      const role = this.currentUser.role

      // MANAGER: Pode filtrar apenas MANAGER e USER
      if (role === 'MANAGER') {
        return this.roleOptions.filter(r => 
          r.value === 'MANAGER' || r.value === 'USER'
        )
      }

      // Outros perfis (PLATFORM_OWNER, PARTNER_ADMIN, TENANT_ADMIN): 
      // Podem ver todos exceto PLATFORM_OWNER
      return this.roleOptions.filter(r => r.value !== 'PLATFORM_OWNER')
    },
  },

  mounted() {
    // Verificar permissão de acesso
    if (!this.canAccessUserManagement) {
      this.showSnackbar('Você não tem permissão para acessar esta página', 'error')
      this.$router.push('/')
      return
    }

    this.loadUsers()
    this.loadDepartments()
  },

  methods: {
    // Verifica se o usuário logado pode editar um usuário específico
    canEditUser(user) {
      // View-only mode desabilita edição para todos
      if (this.isViewOnlyMode) {
        return false
      }

      const loggedUserRole = this.currentUserRole
      const loggedUserId = this.currentUser?.id

      // Sempre pode editar a si mesmo
      if (user.id === loggedUserId) {
        return true
      }

      // PLATFORM_OWNER, PARTNER_ADMIN e TENANT_ADMIN podem editar todos
      if (['PLATFORM_OWNER', 'PARTNER_ADMIN', 'TENANT_ADMIN'].includes(loggedUserRole)) {
        return true
      }

      // MANAGER tem restrições:
      if (loggedUserRole === 'MANAGER') {
        // Pode editar USER (usuários comuns) sempre
        if (user.role === 'USER') {
          return true
        }
        
        // Pode editar MANAGER somente se for o gestor dele (managerId do user é igual ao ID do manager logado)
        if (user.role === 'MANAGER' && user.managerId === loggedUserId) {
          return true
        }
        
        // Não pode editar TENANT_ADMIN, PARTNER_ADMIN, PLATFORM_OWNER
        // Nem MANAGER que não seja subordinado dele
        return false
      }

      // USER não pode editar ninguém (não deveria ter acesso à tela)
      return false
    },

    // === CARREGAR DADOS ===
    async loadUsers() {
      this.loading = true
      try {
        const response = await UsersService.list()
        this.users = response.data || []
        
        // Aplicar filtros locais se houver
        if (this.filterDepartment) {
          this.users = this.users.filter(u => (u.department?.id || u.departmentId) === this.filterDepartment)
        }
        if (this.filterRole) {
          this.users = this.users.filter(u => u.role === this.filterRole)
        }
      } catch (error) {
        console.error('Erro ao carregar usuários:', error)
        this.showSnackbar('Erro ao carregar usuários', 'error')
      } finally {
        this.loading = false
      }
    },

    async loadDepartments() {
      this.loadingDepartments = true
      try {
        const response = await OrganizationService.departments.list()
        const allDepartments = response.data.content || response.data || []
        // Filtrar apenas departamentos ativos
        this.departments = allDepartments.filter(dept => dept.active === true)
      } catch (error) {
        console.error('Erro ao carregar departamentos:', error)
      } finally {
        this.loadingDepartments = false
      }
    },

    async loadTenants() {
      // Carregar tenants apenas para PLATFORM_OWNER
      if (this.currentUserRole !== 'PLATFORM_OWNER') {
        return
      }

      this.loadingTenants = true
      try {
        const response = await TenantsService.list()
        this.tenants = response.data || []
        console.log('✅ Tenants carregados:', this.tenants.length)
      } catch (error) {
        console.error('❌ Erro ao carregar tenants:', error)
        this.showSnackbar('Erro ao carregar lista de tenants', 'error')
      } finally {
        this.loadingTenants = false
      }
    },

    async loadJobRolesByDepartment(departmentId) {
      if (!departmentId) {
        this.filteredJobRoles = []
        return
      }

      this.loadingJobRoles = true
      try {
        const response = await OrganizationService.jobRoles.getByDepartment(departmentId)
        this.filteredJobRoles = response.data || []
      } catch (error) {
        console.error('Erro ao carregar cargos:', error)
        this.filteredJobRoles = []
      } finally {
        this.loadingJobRoles = false
      }
    },

    async loadManagers() {
      this.loadingManagers = true
      try {
        const response = await UsersService.list()
        // Filtrar apenas gestores e admins
        this.managers = (response.data || []).filter(u => 
          u.role === 'MANAGER' || u.role === 'ADMIN'
        )
      } catch (error) {
        console.error('Erro ao carregar gestores:', error)
      } finally {
        this.loadingManagers = false
      }
    },

    // === FILTROS ===
    debouncedSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        // A busca é feita localmente pela data-table do Vuetify
      }, 500)
    },

    async onDepartmentChange(departmentId) {
      this.editedItem.jobRoleId = null
      await this.loadJobRolesByDepartment(departmentId)
    },

    async onTenantChange(tenantId) {
      console.log('🔄 Tenant alterado:', tenantId)
      
      // Limpar departamento e cargo quando trocar de tenant
      this.editedItem.departmentId = null
      this.editedItem.jobRoleId = null
      this.filteredJobRoles = []
      
      // Recarregar departamentos do novo tenant (se necessário)
      // Por enquanto, usamos os departamentos já carregados
      // Futuramente pode implementar: await this.loadDepartmentsByTenant(tenantId)
    },

    // === DIALOG ===
    openCreateDialog() {
      this.editMode = false
      this.editedItem = Object.assign({}, this.defaultItem)
      this.errorMessage = ''
      this.filteredJobRoles = []
      this.dialog = true
      
      // Carregar dados necessários
      this.loadDepartments()
      this.loadManagers()
      
      // Carregar tenants se for PLATFORM_OWNER
      if (this.currentUserRole === 'PLATFORM_OWNER') {
        this.loadTenants()
      }
    },

    editUser(user) {
      this.editMode = true
      // Extrair IDs dos objetos aninhados
      this.editedItem = {
        ...user,
        departmentId: user.department?.id || user.departmentId || null,
        jobRoleId: user.jobRole?.id || user.jobRoleId || null,
        managerId: user.manager?.id || user.managerId || null,
      }
      this.errorMessage = ''
      this.dialog = true
      
      // Carregar dados necessários
      this.loadDepartments()
      this.loadManagers()
      
      // Carregar cargos do departamento do usuário
      const deptId = this.editedItem.departmentId
      if (deptId) {
        this.loadJobRolesByDepartment(deptId)
      }
    },

    closeDialog() {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editMode = false
        this.errorMessage = ''
        this.filteredJobRoles = []
        if (this.$refs.form) {
          this.$refs.form.resetValidation()
        }
      })
    },

    // Formatar telefone enquanto o usuário digita
    formatPhoneInput(value) {
      if (!value) return
      
      // Remove tudo exceto números e sinal de +
      let cleaned = value.replace(/[^\d+]/g, '')
      
      // Garante que começa com +55
      if (!cleaned.startsWith('+')) {
        cleaned = '+55' + cleaned.replace(/^55/, '')
      } else if (!cleaned.startsWith('+55')) {
        cleaned = '+55' + cleaned.substring(1)
      }
      
      // Remove duplicação do código do país
      cleaned = cleaned.replace(/^\+55\+?55/, '+55')
      
      // Limita o tamanho (+55 + 11 dígitos)
      cleaned = cleaned.substring(0, 14)
      
      // Aplica formatação visual: +55 11 99999-8888
      let formatted = cleaned
      if (cleaned.length > 3) {
        formatted = cleaned.substring(0, 3) + ' ' + cleaned.substring(3)
      }
      if (cleaned.length > 5) {
        formatted = formatted.substring(0, 6) + ' ' + formatted.substring(6)
      }
      if (cleaned.length > 10) {
        // Formato com 9 dígitos: +55 11 99999-8888
        formatted = formatted.substring(0, 12) + '-' + formatted.substring(12)
      } else if (cleaned.length > 9) {
        // Formato com 8 dígitos: +55 11 9999-8888
        formatted = formatted.substring(0, 11) + '-' + formatted.substring(11)
      }
      
      this.editedItem.phone = formatted
    },

    // Formatar telefone para o backend (apenas +5511999998888)
    formatPhoneForBackend(phone) {
      if (!phone) return null
      // Remove tudo exceto números e +
      const cleaned = phone.replace(/[^\d+]/g, '')
      // Garante formato +5511999998888
      return cleaned.startsWith('+55') ? cleaned : '+55' + cleaned.replace(/^55/, '')
    },

    async saveUser() {
      if (!this.$refs.form.validate()) {
        return
      }

      this.saving = true
      this.errorMessage = ''

      try {
        // Preparar dados do usuário
        const userData = { ...this.editedItem }
        
        // Formatar telefone para o backend (+5511999998888)
        if (userData.phone) {
          userData.phone = this.formatPhoneForBackend(userData.phone)
        }
        
        // Se não for PLATFORM_OWNER, remover tenantId (backend usará o do contexto)
        if (this.currentUserRole !== 'PLATFORM_OWNER') {
          delete userData.tenantId
        }
        
        console.log('💾 Salvando usuário:', {
          name: userData.name,
          email: userData.email,
          role: userData.role,
          tenantId: userData.tenantId || 'do contexto',
        })

        if (this.editMode) {
          await UsersService.update(userData.id, userData)
          this.showSnackbar('Usuário atualizado com sucesso!', 'success')
        } else {
          const response = await UsersService.create(userData)
          console.log('✅ Usuário criado:', response.data)
          this.showSnackbar('Usuário criado com sucesso!', 'success')
        }

        // Recarregar lista de usuários
        await this.loadUsers()
        
        // Fechar dialog após sucesso
        this.closeDialog()
      } catch (error) {
        console.error('❌ Erro ao salvar usuário:', error)
        
        // Verificar se é realmente um erro (201 é sucesso)
        if (error.response?.status === 201) {
          console.log('✅ Status 201 - Criado com sucesso')
          this.showSnackbar('Usuário criado com sucesso!', 'success')
          await this.loadUsers()
          this.closeDialog()
        } else {
          this.errorMessage = error.response?.data?.message || error.message || 'Erro ao salvar usuário'
          this.showSnackbar(this.errorMessage, 'error')
        }
      } finally {
        this.saving = false
      }
    },

    // === AÇÕES ===
    async toggleUserStatus(user) {
      // Abrir dialog de confirmação
      this.userToToggle = user
      this.statusAction = user.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE'
      this.statusDialog = true
    },

    async confirmToggleStatus() {
      if (!this.userToToggle) return

      this.togglingStatus = true
      try {
        // Usar endpoint específico para atualizar apenas o status (mais performático)
        await UsersService.updateStatus(this.userToToggle.id, this.statusAction)
        this.showSnackbar(
          `Usuário ${this.statusAction === 'ACTIVE' ? 'ativado' : 'desativado'} com sucesso!`,
          'success'
        )
        this.statusDialog = false
        this.userToToggle = null
        this.statusAction = null
        this.loadUsers()
      } catch (error) {
        console.error('Erro ao alterar status:', error)
        this.showSnackbar('Erro ao alterar status do usuário', 'error')
      } finally {
        this.togglingStatus = false
      }
    },

    // === IMPORTAÇÃO CSV ===
    onFileChange(file) {
      if (!file) {
        this.csvContent = ''
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        this.csvContent = e.target.result
      }
      reader.readAsText(file)
    },

    async importUsers() {
      if (!this.csvContent && !this.csvFile) {
        this.showSnackbar('Selecione um arquivo ou cole o conteúdo CSV', 'warning')
        return
      }

      this.importing = true
      this.importResult = null

      try {
        await UsersService.importCSV(this.csvContent)
        
        this.importResult = {
          type: 'success',
          message: 'Importação concluída com sucesso! Atualizando lista...',
        }

        setTimeout(() => {
          this.loadUsers()
          this.closeImportDialog()
        }, 2000)
      } catch (error) {
        console.error('Erro ao importar CSV:', error)
        this.importResult = {
          type: 'error',
          message: error.response?.data || 'Erro ao processar CSV. Verifique o formato.',
        }
      } finally {
        this.importing = false
      }
    },

    closeImportDialog() {
      this.importDialog = false
      this.$nextTick(() => {
        this.csvFile = null
        this.csvContent = ''
        this.importResult = null
      })
    },

    downloadTemplate() {
      const template = 'email,name,role\njoao@empresa.com,João Silva,USER\nmaria@empresa.com,Maria Santos,USER\nusuario@empresa.com,Usuario Sistema,USER'
      
      const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      
      link.setAttribute('href', url)
      link.setAttribute('download', 'template_usuarios.csv')
      link.style.visibility = 'hidden'
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    // === HELPERS ===
    getInitials(name) {
      if (!name) return '?'
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },

    getAvatarColor(name) {
      if (!name) return 'grey'
      const colors = ['primary', 'success', 'info', 'warning', 'error', 'purple', 'indigo', 'teal']
      const index = name.charCodeAt(0) % colors.length
      return colors[index]
    },

    getRoleColor(role) {
      const colors = {
        PLATFORM_OWNER: 'deep-purple',
        PARTNER_ADMIN: 'indigo',
        TENANT_ADMIN: 'red',
        MANAGER: 'purple',
        USER: 'blue',
      }
      return colors[role] || 'grey'
    },

    getRoleLabel(role) {
      const roleOption = this.roleOptions.find(r => r.value === role)
      return roleOption ? roleOption.text : role
    },

    getStatusColor(status) {
      const colors = {
        ACTIVE: '#4ade80',
        DISABLED: '#6b7280',
        PENDING: '#fbbf24',
      }
      return colors[status] || '#6b7280'
    },

    getStatusLabel(status) {
      const labels = {
        ACTIVE: 'Ativo',
        DISABLED: 'Desativado',
        PENDING: 'Pendente',
      }
      return labels[status] || status
    },

    showSnackbar(text, color = 'success') {
      this.snackbar = {
        show: true,
        text: text,
        color: color,
      }
    },
  }
}
</script>

<style scoped>
/* Fundo Global Premium */
.dashboard-container {
  background-color: var(--bg-main, #0D0D0D) !important;
  max-width: 1440px;
  margin: 0 auto;
}
/* ── Page ────────────────────────────────── */
.us-page { padding: 24px 96px; }

/* ── Header ──────────────────────────────── */
.us-header-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(34,211,238,0.12);
  flex-shrink: 0;
}

.us-page-title {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.us-page-sub {
  font-size: 13px;
  margin-top: 2px;
}

.theme--dark .us-page-title { color: #f5f5f5; }
.theme--light .us-page-title { color: #1D1D1F; }
.theme--dark .us-page-sub { color: rgba(255,255,255,0.45); }
.theme--light .us-page-sub { color: rgba(0,0,0,0.5); }

/* ── Buttons ─────────────────────────────── */
.us-btn-new {
  background-color: #22d3ee !important;
  color: #06222b !important;
  font-weight: 700 !important;
  border-radius: 8px !important;
}

.us-btn-import {
  border-radius: 8px !important;
  font-weight: 500 !important;
}

.theme--dark .us-btn-import { border-color: #2a2a2a !important; color: rgba(255,255,255,0.6) !important; }

/* ── Filters ─────────────────────────────── */
.us-filters { flex-wrap: nowrap; }
.us-search { min-width: 0; }
.us-filter { flex-shrink: 0; max-width: 190px; }

/* ── Table ───────────────────────────────── */
.us-table { border-radius: 10px !important; overflow: hidden; }

.theme--dark .us-table { background: transparent !important; }
.theme--light .us-table { background: #ffffff !important; }

/* Header row */
.us-table ::v-deep thead tr th {
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.07em !important;
  text-transform: uppercase !important;
  border-bottom: none !important;
}

.theme--dark .us-table ::v-deep thead tr {
  background-color: #0d0d0d !important;
}
.theme--dark .us-table ::v-deep thead tr th {
  color: rgba(255,255,255,0.4) !important;
}
.theme--light .us-table ::v-deep thead tr {
  background-color: #f8f8f8 !important;
}
.theme--light .us-table ::v-deep thead tr th {
  color: rgba(0,0,0,0.45) !important;
}

/* Body rows */
.theme--dark .us-table ::v-deep tbody tr {
  background-color: #111111 !important;
}
.theme--dark .us-table ::v-deep tbody tr:hover {
  background-color: #161616 !important;
}
.theme--dark .us-table ::v-deep tbody tr td {
  border-bottom: 1px solid #1a1a1a !important;
}
.theme--light .us-table ::v-deep tbody tr:hover {
  background-color: #fafafa !important;
}
.theme--light .us-table ::v-deep tbody tr td {
  border-bottom: 1px solid #f0f0f0 !important;
}

/* User cell */
.theme--dark .us-user-name { color: rgba(255,255,255,0.9); font-size: 14px; }
.theme--light .us-user-name { color: #1D1D1F; font-size: 14px; }
.theme--dark .us-user-email { color: rgba(255,255,255,0.35); }
.theme--light .us-user-email { color: rgba(0,0,0,0.45); }

/* Empty placeholder */
.us-empty { color: rgba(128,128,128,0.5); }

/* Status dot */
.us-status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
}

.us-status-text { font-size: 13px; font-weight: 500; }

/* Action buttons */
.us-action-btn { opacity: 0.5; transition: opacity 0.15s; }
.us-action-btn:hover { opacity: 1; }
.theme--dark .us-action-btn { color: rgba(255,255,255,0.7) !important; }
.theme--light .us-action-btn { color: rgba(0,0,0,0.5) !important; }

/* Footer pagination */
.theme--dark .us-table ::v-deep .v-data-footer {
  background-color: #0d0d0d !important;
  border-top: 1px solid #1a1a1a !important;
  color: rgba(255,255,255,0.5) !important;
}

/* Scrollbar */
.us-table ::v-deep .v-data-table__wrapper { overflow-x: auto; }
</style>

